import type { IntlShape } from 'react-intl'

// Unified quote type
export type CustomerQuote = {
  name: string
  title: string
  pictureUrl: string
  testimonial: string
}

// Typed quote keys
export type CustomerQuoteKey =
  | 'DanFlexDealer'
  | 'FilippoKlarx'
  | 'MartijnSalonizedAdoption'
  | 'EricJobSauceSimplicity'
  | 'DonaldJobTargetSwitch'
  | 'DanielWundertaxAdoption'
  | 'KatsiarynaInteractiveSupport'
  | 'OskarRemoteSenseiSupport'
  | 'JillGrandPadPartnership'
  | 'MartijnVanmoofAdoption'
  | 'LeeLogDNATeamwork'
  | 'JosselinMeeroGrowth'
  | 'KatsiarynaEnteractiveSupport'
  | 'OskarRemoteSenseiResponse'
  | 'JillGrandPadSupport'
  | 'MiguelPVCaseDocumentation'
  | 'ThomasPremiumPlusKnowledge'
  | 'NinaWarneckeNELESupport'
  | 'RomanKubaTsetKnowledgePanel'
  | 'PierreAndreWeCasaSearch'
  | 'ClaireVilletDisplayceAsk'
  | 'AlexisDupontAgorapulseMCP'
  | 'AlexisDupontAgorapulseQuestions'
  | 'AlexisDupontAgorapulseLiveSync'
  | 'AndreFoekenNedapPerplexity'
  | 'NikitaPlaidDiff'
  | 'JacksonKarbonMaintenance'

// Get a single quote
export function getCustomerQuote(
  key: CustomerQuoteKey,
  intl: IntlShape
): CustomerQuote {
  const quotes: Record<CustomerQuoteKey, CustomerQuote> = {
    DanFlexDealer: {
      name: 'Martijn Hazelaar',
      title: intl.formatMessage({
        id: 'quotes.dan.flexdealer.title',
        defaultMessage: 'CTO at Flexdealer',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/a8b78f794c507a8aa92f38d2d37cedd20a5ae2bb-340x340.jpg',
      testimonial: intl.formatMessage({
        id: 'quotes.dan.flexdealer.testimonial',
        defaultMessage:
          "The editing interface and document structure are SO simple and useful. It's been the best Knowledge Base experience we've had.",
      }),
    },
    FilippoKlarx: {
      name: 'Filippo Laita',
      title: intl.formatMessage({
        id: 'quotes.Filippo.klarx.title',
        defaultMessage: 'Senior People Partner at Klarx',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/2c9b4b1acf13d574e063e2f5fa317bbb266aa34e-665x665.jpg',
      testimonial: intl.formatMessage({
        id: 'quotes.Filippo.klarx.testimonial',
        defaultMessage: 'For company Wiki, this is the best.',
      }),
    },
    MartijnSalonizedAdoption: {
      name: 'Martijn Hazelaar',
      title: intl.formatMessage({
        id: 'quotes.martijn.salonized.title',
        defaultMessage: 'Chief Technical Officer at Salonized',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/cfe360abe52db866b291b439a31f20cc64bc414c-120x120.webp',
      testimonial: intl.formatMessage({
        id: 'quotes.martijn.salonized.testimonial',
        defaultMessage:
          "We're growing fast, so ease of adoption is crucial for any new solution - we can't afford to turn back. Slite has proven extremely friendly, for all of our users.",
      }),
    },
    EricJobSauceSimplicity: {
      name: 'Eric Fettner',
      title: intl.formatMessage({
        id: 'quotes.eric.jobsauce.title',
        defaultMessage: 'Chief Operations Officer at The Job Sauce',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/6da84c980827405bfdc9994784e792845788a033-120x120.webp',
      testimonial: intl.formatMessage({
        id: 'quotes.eric.jobsauce.testimonial',
        defaultMessage:
          "Perfect example of a product delivering on the 80/20 rule: you give the top 20% that gets 80% done. No fluff, it's simple.",
      }),
    },
    DonaldJobTargetSwitch: {
      name: 'Donald Sipe',
      title: intl.formatMessage({
        id: 'quotes.donald.jobtarget.title',
        defaultMessage: 'Principal Engineer at Job Target',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/4dc12dcb703dddef7a55b11a69ac572feb7c1f2c-500x500.webp',
      testimonial: intl.formatMessage({
        id: 'quotes.donald.jobtarget.testimonial',
        defaultMessage:
          "We switched from Confluence to Slite because it's easy for the entire company to use, not just the product team. Now, everyone in our company writes and organizes content in Slite.",
      }),
    },
    DanielWundertaxAdoption: {
      name: 'Daniel Hanemann',
      title: intl.formatMessage({
        id: 'quotes.daniel.wundertax.title',
        defaultMessage: 'CEO & Founder at Wundertax',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/364b9854948bcd626409c187d49d8e9275f7bf94-918x918.webp',
      testimonial: intl.formatMessage({
        id: 'quotes.daniel.wundertax.testimonial',
        defaultMessage:
          'Everybody on the team adapted quickly to Slite and actively uses it. The software is highly intuitive, with frequent updates that delight the entire team!',
      }),
    },
    KatsiarynaInteractiveSupport: {
      name: 'Katsiaryna Karliuk',
      title: intl.formatMessage({
        id: 'quotes.katsiaryna.interactive.title',
        defaultMessage: 'Project & Change Manager at Interactive',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/555c78f336ab6858b383c20e2ee4792c5c5e0374-512x512.png',
      testimonial: intl.formatMessage({
        id: 'quotes.katsiaryna.interactive.testimonial',
        defaultMessage:
          "Super impressed with the team's speed and efficiency in replying to our questions.",
      }),
    },
    OskarRemoteSenseiSupport: {
      name: 'Oskar Grochowalski',
      title: intl.formatMessage({
        id: 'quotes.oskar.remotesensei.title',
        defaultMessage: 'CEO at Remote Sensei',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/c163440b54afc429db66e93e9d93f584cecd91c1-512x512.png',
      testimonial: intl.formatMessage({
        id: 'quotes.oskar.remotesensei.testimonial',
        defaultMessage:
          'As always, very swift response from the support team with the fix exactly as requested. We are grateful for the product you created and the service you provide.',
      }),
    },
    JillGrandPadPartnership: {
      name: 'Jill Ferrie',
      title: intl.formatMessage({
        id: 'quotes.jill.grandpad.title',
        defaultMessage: 'Learning & Development Manager at GrandPad',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/b78a2bd7e489538708ae9ab4452d7d118f55b056-512x512.png',
      testimonial: intl.formatMessage({
        id: 'quotes.jill.grandpad.testimonial',
        defaultMessage:
          "We're really grateful for the partnership we've built with Slite.",
      }),
    },
    MartijnVanmoofAdoption: {
      name: 'Martijn Hazelaar',
      title: intl.formatMessage({
        id: 'quotes.martijn.vanmoof.title',
        defaultMessage: 'Head of digital at Vanmoof',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/f055a8386d3a5f1f1b928793cc7fbb1169a1c92d-504x506.webp',
      testimonial: intl.formatMessage({
        id: 'quotes.martijn.vanmoof.testimonial',
        defaultMessage:
          "As we're growing fast, the ease of adoption is a crucial element when choosing a new solution. Slite is friendly, and easy to adopt.",
      }),
    },
    LeeLogDNATeamwork: {
      name: 'Lee Liu',
      title: intl.formatMessage({
        id: 'quotes.lee.logdna.title',
        defaultMessage: 'Co-founder at LogDNA',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/00b956931e9adbc9f07948c12f0ff7ed53facbb0-160x160.webp',
      testimonial: intl.formatMessage({
        id: 'quotes.lee.logdna.testimonial',
        defaultMessage:
          'Our entire team is on the same page and moves faster. Slite lets LogDNA staff operate as a single team across multiple timezones and functions.',
      }),
    },
    JosselinMeeroGrowth: {
      name: 'Josselin Raguenet de Saint Albin',
      title: intl.formatMessage({
        id: 'quotes.josselin.meero.title',
        defaultMessage: 'Head of Customer Service at Meero',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/49c83d88a5713870b07e0f8bc900e5389a5ead18-180x180.png',
      testimonial: intl.formatMessage({
        id: 'quotes.josselin.meero.testimonial',
        defaultMessage:
          'Our team has grown from 250 people to over 700 in the last year, across different offices. Slite supports our fast growth by giving us a structured place to route people when they have questions.',
      }),
    },
    KatsiarynaEnteractiveSupport: {
      name: 'Katsiaryna Karliuk',
      title: intl.formatMessage({
        id: 'quotes.katsiaryna.enteractive.title',
        defaultMessage: 'Project & Change Manager at Enteractive',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/1e63212a79bf399eb83d938e6a401b95470287d8-85x85.png',
      testimonial: intl.formatMessage({
        id: 'quotes.katsiaryna.enteractive.testimonial',
        defaultMessage:
          "We're super impressed with your support team, how fast and efficient they are in replying to all of their questions.",
      }),
    },
    OskarRemoteSenseiResponse: {
      name: 'Oskar Grochowalski',
      title: intl.formatMessage({
        id: 'quotes.oskar.remotesensei2.title',
        defaultMessage: 'CEO at Remote Sensei',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/65569968a6cf856d3006bb9a6c74efc687dcb0c3-85x85.png',
      testimonial: intl.formatMessage({
        id: 'quotes.oskar.remotesensei2.testimonial',
        defaultMessage:
          'As always, very swift response from the team with the fix exactly as requested.',
      }),
    },
    JillGrandPadSupport: {
      name: 'Jill Ferrie',
      title: intl.formatMessage({
        id: 'quotes.jill.grandpad2.title',
        defaultMessage: 'Learning & Development Manager at GrandPad',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/a40701860cf586c538820bccb3e17d2b48755104-85x85.png',
      testimonial: intl.formatMessage({
        id: 'quotes.jill.grandpad2.testimonial',
        defaultMessage:
          "We're really grateful for the partnership we've built with Slite.",
      }),
    },
    MiguelPVCaseDocumentation: {
      name: 'Miguel Herrero',
      title: intl.formatMessage({
        id: 'quotes.miguel.pvcase.title',
        defaultMessage: 'Product Manager at PVCase',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/b03eb15b40747672249d7712b1307fe174a94f7f-280x280.jpg',
      testimonial: intl.formatMessage({
        id: 'quotes.miguel.pvcase.testimonial',
        defaultMessage:
          "Slite helped us significantly reduce escalation times by making our documentation easy to find, always up-to-date, and accessible to everyone. It's become the single source of truth for our company.",
      }),
    },
    ThomasPremiumPlusKnowledge: {
      name: `Thomas D'Hoe`,
      title: intl.formatMessage({
        id: 'quotes.thomas.premiumplus.title',
        defaultMessage: 'Chief Operating Officer at Premium Plus',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/e9b7d9abd8bc70fc5e27a59d4e76ed62acae3442-400x400.jpg',
      testimonial: intl.formatMessage({
        id: 'quotes.thomas.premiumplus.testimonial',
        defaultMessage:
          "Slite transformed how we manage knowledge. It's the perfect balance between simplicity and power - intuitive enough for everyone to use, yet robust enough to handle all our documentation needs.",
      }),
    },
    NinaWarneckeNELESupport: {
      name: 'Nina Warnecke',
      title: intl.formatMessage({
        id: 'quotes.nina.nele.title',
        defaultMessage: 'Learning Designer at NELE',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/8f54e80eed69e6c7b45eaca52688fa76050ccbb9-78x78.jpg',
      testimonial: intl.formatMessage({
        id: 'quotes.nina.nele.testimonial',
        defaultMessage:
          'Thank you so much for the quick help, nice communication and support at all times.',
      }),
    },
    RomanKubaTsetKnowledgePanel: {
      name: 'Roman Kuba',
      title: intl.formatMessage({
        id: 'quotes.roman.tset.title',
        defaultMessage: 'VP Engineering at Tset',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/ea64b58228df3b8ce32e9a4cdd5d57ef0a307f12-400x400.jpg',
      testimonial: intl.formatMessage({
        id: 'quotes.roman.tset.testimonial',
        defaultMessage:
          "I can't imagine dealing with all the content we've produced without the Knowledge Management Panel anymore. It's a life-saver, letting us perform bulk actions and eliminating the need to go over every single doc manually.",
      }),
    },
    PierreAndreWeCasaSearch: {
      name: 'Pierre Andre',
      title: intl.formatMessage({
        id: 'quotes.pierre.wecasa.title',
        defaultMessage: 'Co-Founder at WeCasa',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/5ffd468af695bde9b8db7f2a4bfd201637a66e5a-400x400.jpg',
      testimonial: intl.formatMessage({
        id: 'quotes.pierre.wecasa.testimonial',
        defaultMessage:
          'We tried to build our internal knowledge base before, but the search never worked so nobody could find anything. Slite has completely turned that around, and that changes everything.',
      }),
    },
    ClaireVilletDisplayceAsk: {
      name: 'Claire Villet',
      title: intl.formatMessage({
        id: 'quotes.claire.displayce.title',
        defaultMessage: 'Operations Manager at Displayce',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/e3d59665fc4c5052ab09020729f0a9ede941a307-474x474.jpg',
      testimonial: intl.formatMessage({
        id: 'quotes.claire.displayce.testimonial',
        defaultMessage:
          "Ask has been incredible, it's simplified information gathering for the whole team. We've eliminated the endless 'How does X work' and 'Where can I find Y' messages, saving everyone tons of time.",
      }),
    },
    AlexisDupontAgorapulseMCP: {
      name: 'Alexis Dupont',
      title: intl.formatMessage({
        id: 'quotes.alexis.agorapulse.title2',
        defaultMessage: 'Director of AI Transformation at Agorapulse',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/de32a0eda7701f027c0fb2bb1b6b930dd277b228-512x512.png',
      testimonial: intl.formatMessage({
        id: 'quotes.alexis.agorapulse.testimonial',
        defaultMessage:
          "We use Slite MCP on Claude a lot for documentation. Our team also uses Slite's AI search directly to find docs across Slack, Drive, and more.",
      }),
    },
    AlexisDupontAgorapulseQuestions: {
      name: 'Alexis Dupont',
      title: intl.formatMessage({
        id: 'quotes.alexis.agorapulse.title2',
        defaultMessage: 'Director of AI Transformation at Agorapulse',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/de32a0eda7701f027c0fb2bb1b6b930dd277b228-512x512.png',
      testimonial: intl.formatMessage({
        id: 'quotes.alexis.agorapulse.testimonialQuestions',
        defaultMessage:
          'Since we implemented Slite, the number of questions in Slack has dropped 10x, and everyone can get the information they need.',
      }),
    },
    AlexisDupontAgorapulseLiveSync: {
      name: 'Alexis Dupont',
      title: intl.formatMessage({
        id: 'quotes.alexis.agorapulse.title2',
        defaultMessage: 'Director of AI Transformation at Agorapulse',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/de32a0eda7701f027c0fb2bb1b6b930dd277b228-512x512.png',
      testimonial: intl.formatMessage({
        id: 'quotes.alexis.agorapulse.diyClaude2',
        defaultMessage:
          'Building a script to sync Slack and Salesforce into your docs is easy. Keeping it from breaking your knowledge base is a massive product challenge.',
      }),
    },
    AndreFoekenNedapPerplexity: {
      name: 'Andre Foeken',
      title: intl.formatMessage({
        id: 'quotes.andre.nedap.title',
        defaultMessage: 'CTO at Nedap Healthcare',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/f193548a651a57adba9588832ae5e311603e030b-640x638.jpg',
      testimonial: intl.formatMessage({
        id: 'quotes.andre.nedap.testimonial',
        defaultMessage: 'Like Perplexity for your team knowledge.',
      }),
    },
    // Quote pending the customer's approval — placeholder avatar until we
    // have their photo (tracked in the landing-page doc's open items).
    NikitaPlaidDiff: {
      name: 'Nikita Chepanov',
      title: intl.formatMessage({
        id: 'quotes.nikita.plaid.title3',
        defaultMessage: 'Head of Developer Efficiency at Plaid',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/a16bacdec0f3a293090b06cc075e7c72192896a6-200x200.jpg',
      testimonial: intl.formatMessage({
        id: 'quotes.nikita.plaid.testimonial3',
        defaultMessage:
          'A single runaway agent can break our entire documentation, that’s unacceptable.\nWe need human-in-the-loop enforcement: AI authors the change, a human confirms it.',
      }),
    },
    // Quote pending the customer's approval — placeholder avatar until we
    // have their photo (tracked in the landing-page doc's open items).
    JacksonKarbonMaintenance: {
      name: 'Jackson Leslie',
      title: intl.formatMessage({
        id: 'quotes.jackson.karbon.title',
        defaultMessage: 'Product Manager at Karbon',
      }),
      pictureUrl:
        'https://cdn.sanity.io/images/dnsgqii5/production/e26874c6cd6a13e0212fd1bd628d5018b5fb08c1-800x800.jpg',
      testimonial: intl.formatMessage({
        id: 'quotes.jackson.karbon.testimonial',
        defaultMessage:
          'Documentation maintenance has to be a first-class priority. The most valuable knowledge lives in Slack, in people’s heads, and it never makes it back into the docs.',
      }),
    },
  }

  return quotes[key]
}

// Get multiple quotes
export function getCustomerQuotes(
  keys: CustomerQuoteKey[],
  intl: IntlShape
): CustomerQuote[] {
  return keys.map((key) => getCustomerQuote(key, intl))
}
